import { useContext, useState } from "react";
import { Avatar, Box, Divider, ListItemIcon, Menu, MenuItem, Typography } from "@mui/material";
import { Settings, LightMode, DarkMode, Logout } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useUserInfoContext } from "../../lib/contexts/UserInfoContext";
import { useChatSessionsContext } from "../../lib/contexts/ChatSessionsContext";
import { useCustomThemeContext } from "../../lib/contexts/ThemeContext";
import { logout } from "../../lib/api/Auth";
import { profileContext } from "../../pages/settings/Profile";
import { APP_VERSION } from "../../lib/constants";
import "./AccountMenu.css";

export interface AccountMenuProps {
  onItemClick?: () => void;
  showSettings?: boolean;
}

export default function AccountMenu({ onItemClick, showSettings = true }: AccountMenuProps) {
  const { userInfo, updateUserInfo } = useUserInfoContext();
  const { isDarkMode, toggleDarkMode } = useCustomThemeContext();
  const { resetSelectedChatSession } = useChatSessionsContext();
  const navigate = useNavigate();
  const [profileAnchor, setProfileAnchor] = useState<null | HTMLElement>(null);

  const context = useContext(profileContext);
  if (!context) return null;
  const { setActivateProfile } = context;

  const handleLogout = () => {
    logout();
    updateUserInfo(null);
    resetSelectedChatSession();
    navigate("/");
    onItemClick?.();
  };

  return (
    <>
      <Box
        className="content-panel-profile"
        onClick={(e) => setProfileAnchor(e.currentTarget)}
      >
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: 'var(--color-sage)',
            fontSize: '0.85rem',
            fontWeight: 'bold',
            color: 'white',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            lineHeight: 1,
          }}
        >
          {userInfo?.initial}
        </Avatar>
        <Typography
          variant="body2"
          sx={{ fontWeight: 500, minWidth: 0 }}
          noWrap
        >
          {userInfo?.displayName}
        </Typography>
      </Box>
      <Menu
        anchorEl={profileAnchor}
        open={Boolean(profileAnchor)}
        onClose={() => setProfileAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: { className: 'profile-menu-paper' },
        }}
      >
        {showSettings && (
          <MenuItem
            onClick={() => {
              setProfileAnchor(null);
              setActivateProfile(true);
              onItemClick?.();
            }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <Settings fontSize="small" />
            </ListItemIcon>
            Settings & Profile
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            toggleDarkMode();
            setProfileAnchor(null);
          }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            {isDarkMode ? <LightMode fontSize="small" /> : <DarkMode fontSize="small" />}
          </ListItemIcon>
          {isDarkMode ? 'Light Mode' : 'Dark Mode'}
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            setProfileAnchor(null);
            handleLogout();
          }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            <Logout fontSize="small" />
          </ListItemIcon>
          Sign Out
        </MenuItem>
        {APP_VERSION && (
          <>
            <Divider />
            <Box
              sx={{
                px: 2,
                py: 1,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                userSelect: "all",
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Version
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}
              >
                v{APP_VERSION}
              </Typography>
            </Box>
          </>
        )}
      </Menu>
    </>
  );
}
